/**
 * Token attribute parser shared by the app and scripts/build-traits.ts.
 * This module must stay free of imports so Node and Bun can run it as is.
 */

export interface ParsedAttribute {
  displayType: string | null
  traitType: string
  value: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toAttributeValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

const toAttribute = (entry: unknown): ParsedAttribute | undefined => {
  if (!isRecord(entry) || typeof entry.trait_type !== 'string') {
    return undefined
  }
  const value = toAttributeValue(entry.value)
  if (value === undefined) {
    return undefined
  }
  return {
    displayType:
      typeof entry.display_type === 'string' ? entry.display_type : null,
    traitType: entry.trait_type,
    value
  }
}

const isAttribute = (
  attribute: ParsedAttribute | undefined
): attribute is ParsedAttribute => attribute !== undefined

/**
 * Parses token attributes from a JSON string, an OpenSea-style array of
 * `{trait_type, value, display_type?}` or a `{trait: value}` object.
 * Malformed input and entries without a trait type are skipped.
 */
export const parseAttributes = (raw: unknown): ParsedAttribute[] => {
  let attributes = raw
  if (typeof raw === 'string') {
    try {
      attributes = JSON.parse(raw)
    } catch {
      return []
    }
  }
  if (Array.isArray(attributes)) {
    return attributes.map(toAttribute).filter(isAttribute)
  }
  if (isRecord(attributes)) {
    return Object.entries(attributes)
      .map(([traitType, value]) =>
        toAttribute({ trait_type: traitType, value })
      )
      .filter(isAttribute)
  }
  return []
}
